import { getCruiseItems, getBookingCabinSelectionData } from 'checkout/selectors/cruiseSelectors'
import CruiseSessionCountdownMessage from 'components/Common/CruiseSessionCountdownClock/CruiseSessionCountdownMessage'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import ExperienceReservationCountdownClock from 'checkout/Components/Shared/ExperienceReservation/ExperienceReservationCountdownClock'
import { shouldRenderCruiseSessionCountdownClock } from 'components/Common/CruiseSessionCountdownClock/utils'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useAppSelector } from 'hooks/reduxHooks'
import { flightSearchInProgress, getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import { shouldRenderFlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/utils'
import { isFlightsPendingToAddToCart } from 'checkout/selectors/navigationSelectors'
import { CHECKOUT_SUBHEADER_HEIGHT_CSS_VAR } from 'constants/checkout'
import { getEarliestActiveExperienceReservationExpiresAt } from 'lib/experiences/experienceReservationUtils'

const Container = styled.div`
  ${mediaQueryUp.desktop} {
    position: sticky;
    z-index: ${zIndex.stickyHeaderBanner};
    top: ${props => props.theme.layout.headerFloatingOffset};
    inset-inline: 0;
  }

  &:empty {
    display: none;
  }
`

interface MappedStateProps {
  hasCruise: boolean
  hasExpirableFlights: boolean
  hasFlightsPendingToAddToCart: boolean
  isFetchingFlights: boolean
  earliestExperienceReservationExpiresAt?: string
}

/**
 * Contains high-level banners and elements in the checkout.
 *
 * Becomes sticky on desktop and larger devices.
 */
function CheckoutSubheader(props: MappedStateProps) {
  const {
    hasCruise,
    hasExpirableFlights,
    hasFlightsPendingToAddToCart,
    isFetchingFlights,
    earliestExperienceReservationExpiresAt,
  } = props
  const containerRef = useRef<HTMLDivElement | null>(null)

  const { step = '' } = useParams<'step'>()
  const cabinSelectionData = useAppSelector((state) => getBookingCabinSelectionData(state))

  const showCruiseSessionCountdownClock = useMemo(() => (
    hasCruise &&
    !cabinSelectionData.loading &&
    shouldRenderCruiseSessionCountdownClock(step)
  ), [hasCruise, cabinSelectionData, step])

  const showFlightSessionCountdownClock = useMemo(() => (
    !hasFlightsPendingToAddToCart && shouldRenderFlightSessionCountdownClock(hasExpirableFlights) &&
    !isFetchingFlights &&
    step !== 'lux-plus'
  ), [hasExpirableFlights, hasFlightsPendingToAddToCart, isFetchingFlights, step])

  const showExperienceReservationCountdownClock = useMemo(() => (
    !!earliestExperienceReservationExpiresAt && step !== 'lux-plus'
  ), [earliestExperienceReservationExpiresAt, step])

  useEffect(() => {
    const updateSubheaderHeight = () => {
      const nextHeight = containerRef.current?.getBoundingClientRect().height ?? 0
      document.documentElement.style.setProperty(CHECKOUT_SUBHEADER_HEIGHT_CSS_VAR, `${Math.ceil(nextHeight)}px`)
    }

    updateSubheaderHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined' && containerRef.current
      ? new ResizeObserver(updateSubheaderHeight)
      : null

    if (resizeObserver && containerRef.current) {
      resizeObserver.observe(containerRef.current)
    } else {
      window.addEventListener('resize', updateSubheaderHeight)
    }

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateSubheaderHeight)
    }
  }, [showCruiseSessionCountdownClock, showExperienceReservationCountdownClock, showFlightSessionCountdownClock])

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty(CHECKOUT_SUBHEADER_HEIGHT_CSS_VAR, '0px')
    }
  }, [])

  return <Container id="checkout-sub-header" ref={containerRef}>
    {showCruiseSessionCountdownClock && <PageBanner
      kind="informational"
      align="center"
    >
      <CruiseSessionCountdownMessage />
    </PageBanner>}
    {showFlightSessionCountdownClock && <PageBanner
      kind="informational"
      align="center"
    >
      <FlightSessionCountdownClock />
    </PageBanner>}
    {showExperienceReservationCountdownClock && earliestExperienceReservationExpiresAt && <PageBanner
      kind="informational"
      align="center"
    >
      <ExperienceReservationCountdownClock reservationExpiresAt={earliestExperienceReservationExpiresAt} />
    </PageBanner>}
  </Container>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    hasCruise: getCruiseItems(state).length > 0,
    hasExpirableFlights: getNonCharterFlightItems(state).length > 0,
    hasFlightsPendingToAddToCart: isFlightsPendingToAddToCart(state),
    isFetchingFlights: flightSearchInProgress(state),
    earliestExperienceReservationExpiresAt: getEarliestActiveExperienceReservationExpiresAt(state.checkout.cart.items),
  }
})(CheckoutSubheader)
